import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ChevronLeft, ChevronRight, Play, X } from "lucide-react";
import { FILMS } from "../data/films";
import Chapter from "./Chapter";

const EASE = [0.22, 1, 0.36, 1];

// Orden cronológico del catálogo (los placeholders sin año van al final)
const SORTED = [...FILMS].sort(
    (a, b) => (parseInt(a.anio, 10) || 9999) - (parseInt(b.anio, 10) || 9999)
);

// Póster tipográfico provisional (se reemplaza por la imagen real en films.js)
function Poster({ film, large, contain }) {
    const src = film.posterOficial || film.poster;
    if (src) {
        const alt = film.posterOficial
            ? film.posterOficialAlt
            : film.posterAlt || `Póster oficial de la película ${film.titulo} (${film.anio})`;
        return (
            <img
                src={src}
                alt={alt}
                className={contain ? "max-h-[46vh] w-auto" : "h-full w-full object-cover"}
                loading="lazy"
            />
        );
    }
    return (
        <div className="relative flex h-full w-full flex-col justify-between p-6" style={{ backgroundColor: film.tono }}>
            <div className="jf-vignette absolute inset-0" aria-hidden="true" />
            <div className="jf-perf absolute inset-y-0 left-2 w-2.5 opacity-40" aria-hidden="true" />
            <div className="jf-perf absolute inset-y-0 right-2 w-2.5 opacity-40" aria-hidden="true" />
            <div className="relative flex items-center justify-between font-mono-jf text-[10px] uppercase tracking-[0.25em] text-[#FAF8F5]/60">
                <span>Jaqueca Films</span>
                <span>{film.anio}</span>
            </div>
            <h3
                className={`relative font-display leading-[1.02] text-[#FAF8F5] ${
                    large ? "text-4xl sm:text-5xl" : "text-3xl"
                }`}
            >
                {film.titulo}
            </h3>
            <div className="relative flex items-center justify-between font-mono-jf text-[10px] uppercase tracking-[0.25em] text-[#FAF8F5]/60">
                <span>{film.tituloEn}</span>
                <span>2.39:1</span>
            </div>
        </div>
    );
}

export default function Films({ setLocked }) {
    const [active, setActive] = useState(null);
    const trackRef = useRef(null);

    useEffect(() => {
        if (!active) return;
        setLocked(true);
        const onKey = (e) => e.key === "Escape" && setActive(null);
        window.addEventListener("keydown", onKey);
        return () => {
            setLocked(false);
            window.removeEventListener("keydown", onKey);
        };
    }, [active, setLocked]);

    // Distribución: carrusel horizontal (scroll por flechas)
    const scrollTrack = (dir) =>
        trackRef.current?.scrollBy({ left: dir * trackRef.current.clientWidth * 0.8, behavior: "smooth" });

    return (
        <section id="peliculas" className="mx-auto max-w-[1400px] px-6 py-24 sm:px-10 sm:py-32" data-testid="films-section">
            <Chapter num="01" eyebrow="Catálogo cinematográfico" title="Películas" />
            {/* Carrusel horizontal: tarjetas uniformes en orden cronológico */}
            <div className="relative">
                <div
                    ref={trackRef}
                    className="flex snap-x snap-mandatory gap-6 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                    data-testid="films-carousel"
                >
                {SORTED.map((film, i) => (
                    <motion.article
                        key={film.slug}
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.7, ease: EASE, delay: Math.min(i, 4) * 0.07 }}
                        className="group w-[78vw] shrink-0 snap-start sm:w-[330px]"
                        data-testid={`film-card-${film.slug}`}
                    >
                        <button
                            onClick={() => setActive(film)}
                            className="block w-full text-left"
                            aria-label={`Ver ficha de ${film.titulo}`}
                            data-testid={`film-open-${film.slug}`}
                        >
                            <div className="relative aspect-[2/3] overflow-hidden">
                                <div className="h-full w-full transition-transform duration-700 ease-out group-hover:scale-[1.04]">
                                    <Poster film={film} large={i < 2} />
                                </div>
                                <div className="absolute inset-0 flex items-end justify-between bg-[#141312]/0 p-5 transition-colors duration-500 group-hover:bg-[#141312]/25">
                                    <span className="translate-y-3 font-mono-jf text-[10px] uppercase tracking-[0.25em] text-[#FAF8F5] opacity-0 transition-[opacity,transform] duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                                        Ver ficha
                                    </span>
                                    <ArrowUpRight
                                        size={20}
                                        className="translate-y-3 text-[#FAF8F5] opacity-0 transition-[opacity,transform] duration-500 group-hover:translate-y-0 group-hover:opacity-100"
                                    />
                                </div>
                            </div>
                            <div className="mt-4 flex h-[3.4rem] items-baseline justify-between gap-4">
                                <h3 className="font-display text-xl leading-tight tracking-tight text-[#141312] sm:text-2xl">
                                    {film.titulo}
                                </h3>
                                <span className="shrink-0 font-mono-jf text-xs text-[#6B1D2F]">{film.anio}</span>
                            </div>
                            <p className="mt-1 font-mono-jf text-[11px] uppercase tracking-[0.2em] text-[#736E65]">
                                {film.ficha}
                            </p>
                            <p className="jf-clamp-3 mt-3 min-h-[3.9rem] text-sm leading-relaxed text-[#524E48]">{film.sinopsis}</p>
                        </button>
                    </motion.article>
                ))}
                </div>
                {/* Flechas del carrusel */}
                <div className="mt-8 flex items-center justify-between">
                    <p className="font-mono-jf text-[10px] uppercase tracking-[0.25em] text-[#736E65]" data-testid="films-count">
                        {String(SORTED.length).padStart(2, "0")} películas
                    </p>
                    <div className="flex gap-3">
                        <button
                            onClick={() => scrollTrack(-1)}
                            className="rounded-full border border-[#141312]/20 p-3 text-[#141312] transition-colors duration-300 hover:border-[#6B1D2F] hover:bg-[#6B1D2F] hover:text-[#FAF8F5]"
                            aria-label="Películas anteriores"
                            data-testid="films-prev"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <button
                            onClick={() => scrollTrack(1)}
                            className="rounded-full border border-[#141312]/20 p-3 text-[#141312] transition-colors duration-300 hover:border-[#6B1D2F] hover:bg-[#6B1D2F] hover:text-[#FAF8F5]"
                            aria-label="Películas siguientes"
                            data-testid="films-next"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Ficha de película (modal) */}
            <AnimatePresence>
                {active && (
                    <div className="fixed inset-0 z-[90] flex items-end justify-center sm:items-center sm:p-8">
                        <motion.button
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setActive(null)}
                            className="absolute inset-0 bg-[#141312]/80 backdrop-blur-sm"
                            aria-label="Cerrar ficha"
                            data-testid="film-modal-backdrop"
                        />
                        <motion.div
                            role="dialog"
                            aria-modal="true"
                            aria-label={`Ficha de ${active.titulo}`}
                            initial={{ opacity: 0, y: 60, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 40, scale: 0.98 }}
                            transition={{ duration: 0.45, ease: EASE }}
                            className="relative max-h-[92vh] w-full max-w-2xl overflow-y-auto bg-[#FAF8F5] shadow-2xl [scrollbar-color:#6B1D2F_#EDE8DE] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-2.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#6B1D2F]/60 [&::-webkit-scrollbar-track]:bg-[#EDE8DE]"
                            data-testid="film-modal"
                        >
                            {active.posterOficial ? (
                                <div className="flex justify-center bg-[#141312] px-8 py-8">
                                    <Poster film={active} contain />
                                </div>
                            ) : (
                                <div className="relative aspect-video w-full overflow-hidden">
                                    <Poster film={active} large />
                                </div>
                            )}
                            <div className="relative p-8 sm:p-10">
                                <button
                                    onClick={() => setActive(null)}
                                    className="absolute right-5 top-5 rounded-full border border-[#E5E0D5] p-2 text-[#141312] transition-colors duration-300 hover:bg-[#6B1D2F] hover:text-[#FAF8F5]"
                                    aria-label="Cerrar"
                                    data-testid="film-modal-close"
                                >
                                    <X size={16} />
                                </button>
                                <p className="font-mono-jf text-[11px] uppercase tracking-[0.25em] text-[#6B1D2F]">
                                    {active.ficha}
                                </p>
                                <h3 className="mt-4 font-display text-3xl leading-tight tracking-tight text-[#141312] sm:text-4xl" data-testid="film-modal-title">
                                    {active.titulo}
                                </h3>
                                <p className="mt-1 font-display text-lg italic text-[#736E65]">{active.tituloEn}</p>
                                <div className="my-6 h-px w-full bg-[#E5E0D5]" />
                                <p className="text-sm leading-relaxed text-[#524E48] sm:text-base" data-testid="film-modal-synopsis">
                                    {active.sinopsis}
                                </p>
                                {active.trailer && (
                                    <a
                                        href={active.trailer}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-8 inline-flex items-center gap-3 rounded-full border border-[#141312] px-6 py-3 font-mono-jf text-xs uppercase tracking-[0.2em] text-[#141312] transition-colors duration-300 hover:border-[#6B1D2F] hover:bg-[#6B1D2F] hover:text-[#FAF8F5]"
                                        data-testid="film-modal-trailer"
                                    >
                                        <Play size={14} /> Ver tráiler en Vimeo
                                    </a>
                                )}
                                <p className="mt-8 font-mono-jf text-[10px] uppercase tracking-[0.25em] text-[#736E65]">
                                    Una producción de Jaqueca Films
                                </p>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </section>
    );
}
